import React, { useEffect, useState } from "react";
import {
	Box,
	Divider,
	Drawer,
	FormControl,
	IconButton,
	MenuItem,
	Select,
	Slider,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
	Chip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Checkbox,
	FormControlLabel,
	ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import TuneIcon from "@mui/icons-material/Tune";
import { debounce } from "lodash";

const Filters = ({
	categories,
	categoryTypes,
	outboundDepartures,
	outboundArrivals,
	inboundDepartures,
	inboundArrivals,
	camps,
	roomTypes = [],
	providers,
	madinahHotelZone,
	makkahHotelZone,
	makkah2HotelZone,
	selectedCategory,
	setSelectedCategory,
	selectedCategoryType,
	setSelectedCategoryType,
	selectedOutboundDeparture,
	setSelectedOutboundDeparture,
	selectedOutboundArrival,
	setSelectedOutboundArrival,
	selectedInboundDeparture,
	setSelectedInboundDeparture,
	selectedInboundArrival,
	setSelectedInboundArrival,
	selectedProvider,
	setSelectedProvider,
	selectedCamp,
	setSelectedCamp,
	selectedRoomTypes = [],
	setSelectedRoomTypes = () => {},
	includeSoldOut = false,
	setIncludeSoldOut = () => {},
	selectedMadinahHotelZone,
	setSelectedMadinahHotelZone,
	selectedMakkahHotelZone,
	setSelectedMakkahHotelZone,
	selectedMakkah2HotelZone,
	setSelectedMakkah2HotelZone,
	searchQuery,
	setSearchQuery,
	priceRangeSAR,
	setPriceRangeSAR,
	priceRangeGBP,
	setPriceRangeGBP,
	duration,
	setDuration,
	minPriceSAR = 0,
	maxPriceSAR = 100000,
	minPriceGBP = 0,
	maxPriceGBP = 20000,
	minDuration = 0,
	maxDuration = 45,
	selectedCurrency = "GBP",
	currencySymbols = {},
}) => {
	const sarSymbol = "⃁";

	const handleChangePriceRangeSAR = debounce((event, newValue) => {
		setPriceRangeSAR(newValue);
	}, 100);

	const handleChangePriceRangeGBP = debounce((event, newValue) => {
		setPriceRangeGBP(newValue);
	}, 100);

	const handleChangeDuration = debounce((event, newValue) => {
		setDuration(newValue);
	}, 100);

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

	useEffect(() => {
		if (isDesktop) {
			setIsDrawerOpen(false);
		}
	}, [isDesktop]);

	const toggleDrawer = (open) => () => setIsDrawerOpen(open);

	const [expandedAccordion, setExpandedAccordion] = useState("price");

	const handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpandedAccordion(isExpanded ? panel : false);
	};

	const toggleSelection = (value, selected, setSelected) => {
		if (selected.includes(value)) {
			setSelected(selected.filter((item) => item !== value));
		} else {
			setSelected([...selected, value]);
		}
	};

	const renderCheckboxList = (items, selected, setSelected) => (
		<Box sx={{ maxHeight: 220, overflowY: "auto", pr: 1 }}>
			{items.map((item) => (
				<FormControlLabel
					key={item}
					sx={{ m: 0 }}
					control={
						<Checkbox
							size="small"
							checked={selected.includes(item)}
							onChange={() => toggleSelection(item, selected, setSelected)}
						/>
					}
					label={
						<Typography fontSize="0.85rem" color="text.secondary">
							{item}
						</Typography>
					}
				/>
			))}
		</Box>
	);

	// Helper function to check if filter should be shown (has more than 1 option)
	const shouldShowFilter = (items) => {
		const uniqueItems = items?.filter(item => item && item !== "" && item !== null && item !== undefined) || [];
		return uniqueItems.length > 1;
	};

	// Get currency symbol for display
	const currencySymbol = currencySymbols[selectedCurrency] || selectedCurrency;

	const activeFiltersCount = [
		selectedCategory?.length > 0,
		selectedCategoryType?.length > 0,
		selectedProvider?.length > 0,
		selectedCamp?.length > 0,
		selectedRoomTypes?.length > 0,
		!includeSoldOut,
		selectedOutboundDeparture,
		selectedOutboundArrival,
		selectedMadinahHotelZone?.length > 0,
		selectedMakkahHotelZone?.length > 0,
		selectedMakkah2HotelZone?.length > 0,
		searchQuery,
	].filter(Boolean).length;

	return (
		<>
			{/* Mobile Filter Button */}
			<IconButton
				data-filter-button
				sx={{
					display: { xs: "flex", md: "none" },
					position: "fixed",
					right: 20,
					bottom: 20,
					zIndex: 1000,
					bgcolor: "#002366",
					color: "#fff",
					width: 56,
					height: 56,
					boxShadow: "0 4px 20px rgba(0,35,102,0.4)",
					"&:hover": {
						bgcolor: "#001a4d",
						boxShadow: "0 6px 24px rgba(0,35,102,0.5)",
					},
				}}
				onClick={toggleDrawer(true)}
			>
				<FilterListIcon sx={{ fontSize: 28 }} />
			</IconButton>

			{/* Drawer for Mobile */}
			<Drawer
				anchor="right"
				open={isDrawerOpen}
				onClose={toggleDrawer(false)}
				sx={{
					"& .MuiDrawer-paper": {
						width: { xs: "85%", sm: "400px" },
						maxWidth: "400px",
					},
				}}
			>
				<Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
					<Box
						sx={{
							p: 3,
							bgcolor: "#002366",
							color: "#fff",
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<TuneIcon />
							<Typography variant="h6" fontWeight={700}>
								Filters
							</Typography>
						</Box>
						<IconButton
							onClick={toggleDrawer(false)}
							sx={{ color: "#fff" }}
						>
							<ClearIcon />
						</IconButton>
					</Box>
					<Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
						{renderFilters()}
					</Box>
				</Box>
			</Drawer>

			{/* Standard Filter UI for Large Screens */}
			{isDesktop && (
				<Box
					sx={{
						width: "100%",
						bgcolor: "background.paper",
						borderRadius: "16px",
						boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
						border: "1px solid rgba(0,0,0,0.06)",
						overflow: "hidden",
					}}
				>
					<Box sx={{ p: 2, bgcolor: "rgba(0,35,102,0.02)", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
						<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<TuneIcon sx={{ color: "#002366", fontSize: 20 }} />
								<Typography variant="subtitle1" fontWeight={700} color="#002366">
									Filters
								</Typography>
							</Box>
							{activeFiltersCount > 0 && (
								<Chip
									label={`${activeFiltersCount} active`}
									size="small"
									sx={{
										height: 28,
										bgcolor: "#002366",
										color: "#fff",
										fontWeight: 700,
										borderRadius: "8px",
									}}
								/>
							)}
						</Box>
					</Box>
					<Box sx={{ p: 2 }}>
						{renderFilters()}
					</Box>
				</Box>
			)}
		</>
	);

	function renderFilters() {
		return (
			<>
				{/* Search */}
				<TextField
					label="Search packages"
					variant="outlined"
					fullWidth
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					InputProps={{
						startAdornment: <SearchIcon sx={{ mr: 1, color: "#002366" }} />,
						sx: {
							bgcolor: "#fff",
							borderRadius: "12px",
							"&:hover": {
								borderColor: "#002366",
							},
							"&.Mui-focused": {
								borderColor: "#002366",
								boxShadow: "0 0 0 3px rgba(0,35,102,0.1)",
							},
						},
					}}
					sx={{ mb: 2 }}
				/>

				<Box
					sx={{
						mb: 2,
						px: 1,
						py: 1,
						borderRadius: "10px",
						border: "1px solid rgba(0,0,0,0.06)",
						bgcolor: "rgba(211,47,47,0.04)",
					}}
				>
					<FormControlLabel
						control={
							<Checkbox
								size="small"
								checked={includeSoldOut}
								onChange={(e) => setIncludeSoldOut(e.target.checked)}
							/>
						}
						label={
							<Typography fontSize="0.85rem" color="text.secondary">
								Show sold out packages
							</Typography>
						}
					/>
				</Box>

				{/* Price SAR Accordion */}
				<Accordion
					expanded={expandedAccordion === "price"}
					onChange={handleAccordionChange("price")}
					sx={{
						mb: 1.5,
						border: "1px solid rgba(0,0,0,0.06)",
						borderRadius: "10px !important",
						boxShadow: "none",
						"&:before": { display: "none" },
						"&.Mui-expanded": { margin: "0 0 6px 0" },
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						sx={{
							minHeight: 40,
							bgcolor: expandedAccordion === "price" ? "rgba(0,35,102,0.02)" : "transparent",
							borderRadius: "10px",
							"&.Mui-expanded": { minHeight: 40 },
							"& .MuiAccordionSummary-content": { margin: "8px 0" },
						}}
					>
						<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
							Price Range ({sarSymbol})
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ pt: 0 }}>
						<Box sx={{ px: 1, pb: 1 }}>
							<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block", fontWeight: 600 }}>
								{sarSymbol} {priceRangeSAR[0].toLocaleString()} - {priceRangeSAR[1].toLocaleString()}
							</Typography>
							<Slider
								value={priceRangeSAR}
								onChange={handleChangePriceRangeSAR}
								valueLabelDisplay="auto"
								min={minPriceSAR}
								max={maxPriceSAR}
								step={100}
								sx={{
									color: "#002366",
									"& .MuiSlider-thumb": {
										width: 20,
										height: 20,
										boxShadow: "0 2px 8px rgba(0,35,102,0.3)",
									},
									"& .MuiSlider-track": {
										height: 6,
										borderRadius: 3,
									},
									"& .MuiSlider-rail": {
										height: 6,
										borderRadius: 3,
										bgcolor: "rgba(0,0,0,0.1)",
									},
								}}
							/>
						</Box>
					</AccordionDetails>
				</Accordion>

				{/* Price in Selected Currency Accordion */}
				<Accordion
					expanded={expandedAccordion === "pricegbp"}
					onChange={handleAccordionChange("pricegbp")}
					sx={{
						mb: 1.5,
						border: "1px solid rgba(0,0,0,0.06)",
						borderRadius: "10px !important",
						boxShadow: "none",
						"&:before": { display: "none" },
						"&.Mui-expanded": { margin: "0 0 6px 0" },
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						sx={{
							minHeight: 40,
							bgcolor: expandedAccordion === "pricegbp" ? "rgba(0,35,102,0.02)" : "transparent",
							borderRadius: "10px",
							"&.Mui-expanded": { minHeight: 40 },
							"& .MuiAccordionSummary-content": { margin: "8px 0" },
						}}
					>
						<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
							Price Range ({currencySymbol})
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ pt: 0 }}>
						<Box sx={{ px: 1, pb: 1 }}>
							<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block", fontWeight: 600 }}>
								{currencySymbol}{priceRangeGBP[0].toLocaleString()} - {currencySymbol}{priceRangeGBP[1].toLocaleString()}
							</Typography>
							<Slider
								value={priceRangeGBP}
								onChange={handleChangePriceRangeGBP}
								valueLabelDisplay="auto"
								min={minPriceGBP}
								max={maxPriceGBP}
								step={selectedCurrency === 'JPY' ? 1000 : 100}
								sx={{
									color: "#002366",
									"& .MuiSlider-thumb": {
										width: 20,
										height: 20,
										boxShadow: "0 2px 8px rgba(0,35,102,0.3)",
									},
									"& .MuiSlider-track": {
										height: 6,
										borderRadius: 3,
									},
									"& .MuiSlider-rail": {
										height: 6,
										borderRadius: 3,
										bgcolor: "rgba(0,0,0,0.1)",
									},
								}}
							/>
						</Box>
					</AccordionDetails>
				</Accordion>

				{/* Duration Accordion */}
				<Accordion
					expanded={expandedAccordion === "duration"}
					onChange={handleAccordionChange("duration")}
					sx={{
						mb: 1.5,
						border: "1px solid rgba(0,0,0,0.06)",
						borderRadius: "10px !important",
						boxShadow: "none",
						"&:before": { display: "none" },
						"&.Mui-expanded": { margin: "0 0 6px 0" },
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						sx={{
							minHeight: 40,
							bgcolor: expandedAccordion === "duration" ? "rgba(0,35,102,0.02)" : "transparent",
							borderRadius: "10px",
							"&.Mui-expanded": { minHeight: 40 },
							"& .MuiAccordionSummary-content": { margin: "8px 0" },
						}}
					>
						<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
							Duration - Days
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ pt: 0 }}>
						<Box sx={{ px: 1, pb: 1 }}>
							<Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block", fontWeight: 600 }}>
								{duration[0]} - {duration[1]} days
							</Typography>
							<Slider
								value={duration}
								onChange={handleChangeDuration}
								valueLabelDisplay="auto"
								min={minDuration}
								max={maxDuration}
								step={1}
								sx={{
									color: "#002366",
									"& .MuiSlider-thumb": {
										width: 20,
										height: 20,
										boxShadow: "0 2px 8px rgba(0,35,102,0.3)",
									},
									"& .MuiSlider-track": {
										height: 6,
										borderRadius: 3,
									},
									"& .MuiSlider-rail": {
										height: 6,
										borderRadius: 3,
										bgcolor: "rgba(0,0,0,0.1)",
									},
								}}
							/>
						</Box>
					</AccordionDetails>
				</Accordion>

				<Divider sx={{ my: 2 }} />

				{/* Category Type */}
				{shouldShowFilter(categoryTypes) && (
					<Accordion
						expanded={expandedAccordion === "categoryType"}
						onChange={handleAccordionChange("categoryType")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "categoryType" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Category Type {selectedCategoryType.length > 0 && `(${selectedCategoryType.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedCategoryType.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedCategoryType([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(categoryTypes, selectedCategoryType, setSelectedCategoryType)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Category */}
				{shouldShowFilter(categories) && (
					<Accordion
						expanded={expandedAccordion === "category"}
						onChange={handleAccordionChange("category")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "category" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Category {selectedCategory.length > 0 && `(${selectedCategory.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedCategory.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedCategory([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(categories, selectedCategory, setSelectedCategory)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Providers */}
				{shouldShowFilter(providers) && (
					<Accordion
						expanded={expandedAccordion === "provider"}
						onChange={handleAccordionChange("provider")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "provider" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Provider {selectedProvider.length > 0 && `(${selectedProvider.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedProvider.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedProvider([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(providers, selectedProvider, setSelectedProvider)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Camps */}
				{shouldShowFilter(camps) && (
					<Accordion
						expanded={expandedAccordion === "camp"}
						onChange={handleAccordionChange("camp")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "camp" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Camp {selectedCamp.length > 0 && `(${selectedCamp.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedCamp.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedCamp([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(camps, selectedCamp, setSelectedCamp)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Occupancy */}
				{shouldShowFilter(roomTypes) && (
					<Accordion
						expanded={expandedAccordion === "occupancy"}
						onChange={handleAccordionChange("occupancy")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "occupancy" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Occupancy {selectedRoomTypes.length > 0 && `(${selectedRoomTypes.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedRoomTypes.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedRoomTypes([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(roomTypes, selectedRoomTypes, setSelectedRoomTypes)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Outbound Flights */}
				{shouldShowFilter(outboundDepartures) && (
					<Accordion
						expanded={expandedAccordion === "outbound"}
						onChange={handleAccordionChange("outbound")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "outbound" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Outbound Flight
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
								<FormControl fullWidth size="small">
									<Select
										value={selectedOutboundDeparture}
										onChange={(e) => setSelectedOutboundDeparture(e.target.value)}
										displayEmpty
										renderValue={(selected) => {
											if (!selected) return <em>All Departures</em>;
											return selected;
										}}
										sx={{
											borderRadius: "10px",
											fontSize: "0.85rem",
											"&:hover .MuiOutlinedInput-notchedOutline": {
												borderColor: "#002366",
											},
											"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
												borderColor: "#002366",
												borderWidth: 2,
											},
										}}
										endAdornment={
											selectedOutboundDeparture && (
												<IconButton
													size="small"
													onClick={(e) => {
														e.stopPropagation();
														setSelectedOutboundDeparture("");
													}}
													sx={{ mr: 2, position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)' }}
												>
													<ClearIcon fontSize="small" />
												</IconButton>
											)
										}
									>
										{outboundDepartures.map((departure) => (
											<MenuItem key={departure} value={departure} sx={{ py: 0, minHeight: 32 }}>
												<ListItemText primary={departure} primaryTypographyProps={{ fontSize: '0.85rem', color: 'text.secondary' }} />
											</MenuItem>
										))}
									</Select>
								</FormControl>
								{/* Arrivals filter hidden by request */}
							</Box>
						</AccordionDetails>
					</Accordion>
				)}

				<Divider sx={{ my: 2 }} />

				{/* Zone Filtering Section Header */}
				<Typography
					variant="caption"
					sx={{
						display: 'block',
						mb: 1,
						mt: 0.5,
						color: '#64748b',
						fontWeight: 600,
						fontSize: '0.65rem',
						textTransform: 'uppercase',
						letterSpacing: 0.8,
					}}
				>
					Hotel Zone Filters
				</Typography>

				{/* Makkah Hotel 1 Zone */}
				{shouldShowFilter(makkahHotelZone) && (
					<Accordion
						expanded={expandedAccordion === "makkahHotelZone"}
						onChange={handleAccordionChange("makkahHotelZone")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "makkahHotelZone" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Makkah Hotel 1 Zone {selectedMakkahHotelZone.length > 0 && `(${selectedMakkahHotelZone.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedMakkahHotelZone.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedMakkahHotelZone([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(makkahHotelZone, selectedMakkahHotelZone, setSelectedMakkahHotelZone)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Makkah Hotel 2 Zone */}
				{shouldShowFilter(makkah2HotelZone) && (
					<Accordion
						expanded={expandedAccordion === "makkah2HotelZone"}
						onChange={handleAccordionChange("makkah2HotelZone")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "makkah2HotelZone" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Makkah Hotel 2 Zone {selectedMakkah2HotelZone.length > 0 && `(${selectedMakkah2HotelZone.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedMakkah2HotelZone.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedMakkah2HotelZone([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(makkah2HotelZone, selectedMakkah2HotelZone, setSelectedMakkah2HotelZone)}
						</AccordionDetails>
					</Accordion>
				)}

				{/* Madinah Hotel Zone */}
				{shouldShowFilter(madinahHotelZone) && (
					<Accordion
						expanded={expandedAccordion === "madinahHotelZone"}
						onChange={handleAccordionChange("madinahHotelZone")}
						sx={{
							mb: 1.5,
							border: "1px solid rgba(0,0,0,0.06)",
							borderRadius: "10px !important",
							boxShadow: "none",
							"&:before": { display: "none" },
							"&.Mui-expanded": { margin: "0 0 6px 0" },
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							sx={{
								minHeight: 40,
								bgcolor: expandedAccordion === "madinahHotelZone" ? "rgba(0,35,102,0.02)" : "transparent",
								borderRadius: "10px",
								"&.Mui-expanded": { minHeight: 40 },
								"& .MuiAccordionSummary-content": { margin: "8px 0" },
							}}
						>
							<Typography fontWeight={600} fontSize="0.875rem" color="text.secondary">
								Madinah Hotel Zone {selectedMadinahHotelZone.length > 0 && `(${selectedMadinahHotelZone.length})`}
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
							{selectedMadinahHotelZone.length > 0 && (
								<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
									<IconButton
										size="small"
										onClick={() => setSelectedMadinahHotelZone([])}
									>
										<ClearIcon fontSize="small" />
									</IconButton>
								</Box>
							)}
							{renderCheckboxList(madinahHotelZone, selectedMadinahHotelZone, setSelectedMadinahHotelZone)}
						</AccordionDetails>
					</Accordion>
				)}
			</>
		);
	}
};

export default Filters;
